import { Box, Card, CardContent, Tooltip, Typography } from '@mui/material';
import { withStyles } from '@material-ui/core';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Spinner from 'modules/common/spinner.component';
import { getEnumColumns } from 'modules/common/enumTableColumn';
import ClientAddressManagementTable from './clientAddressManagementTable.component';
import { postClientAddress, updateClientAddress, deleteClientAddress, getClientAddressesByClientId } from 'modules/clients/clients.actions';
import { showToast } from 'modules/layout/layout.actions';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import {
  POST_CLIENT_ADDRESS_FAILURE,
  UPDATE_CLIENT_ADDRESS_FAILURE,
  DELETE_CLIENT_ADDRESS_FAILURE
} from 'modules/clients/types';

const styles = theme => ({
  wideControl: {
    minWidth: 174
  },
  section: {
    marginBottom: theme.spacing(2)
  },
  card: {
    width: '100%',
    maxWidth: 1500,
    flexGrow: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  cardContainer: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  iconRow: {
    display: 'flex',
    flexDirection: 'row'
  },
  cardDisplayContent: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    margin: '10px'
  }
});

const AddressesCard = props => {
  const { clientAddresses, clientId, hideEdit, enumerations } = props;
  const [isLoading, setIsLoading] = useState(false);

  const countries =
    enumerations && enumerations.countries && enumerations.countries.length > 0
      ? enumerations.countries.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const provinces =
    enumerations && enumerations.provinces && enumerations.provinces.length > 0
      ? enumerations.provinces.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const { countryColumn, provinceColumn } = getEnumColumns(enumerations);

  const addressColumns = [
    { header: 'Street Address', accessorKey: 'addressLine1', type: 'text', required: true },
    { header: 'Apartment/Suite/Floor', accessorKey: 'addressLine2', type: 'text', hideInTable: true, required: false },
    { header: 'Additional Address Info', accessorKey: 'addressLine3', type: 'text', hideInTable: true, required: false },
    { header: 'City', accessorKey: 'city', type: 'text', required: false },
    { header: 'County', accessorKey: 'county', type: 'text', hideInTable: true, required: false },
    { header: 'Zip Code', accessorKey: 'zipcode', type: 'text', hideInTable: false, required: false },
    { header: 'Country', ...countryColumn('countryId'), type: 'dropdown-v2', options: countries, required: true },
    { header: 'State', ...provinceColumn('provinceId'), type: 'dropdown-v2', options: provinces, required: false }
  ];

  const updateAddress = async (address, values) => {
    const addressId = address.original.addressId;

    const newValues = {
      clientAddress: {
        clientAddressId: address.original.clientAddressId,
        clientRecordId: address.original.clientRecordId,
        isPrimary: values.isPrimary ? values.isPrimary : false,
        addressId: addressId
      },
      address: {
        addressId: addressId,
        addressLine1: values.addressLine1 ? values.addressLine1 : '',
        addressLine2: values.addressLine2 ? values.addressLine2 : '',
        addressLine3: values.addressLine3 ? values.addressLine3 : '',
        city: values.city ? values.city : '',
        county: values.county ? values.county : '',
        zipcode: values.zipcode ? values.zipcode : '',
        countryId: values.countryId ? values.countryId : '',
        provinceId: values.provinceId ? values.provinceId : '',
        addressTypeId: "67a86dee-2aa4-4975-9df0-95368dfec694",
        addedBy: 1,
        addedOn: new Date(),
        modifiedBy: 1,
        modifiedOn: new Date()
      }
    };

    const resp = await props.updateClientAddress(newValues);
    if (resp.type === UPDATE_CLIENT_ADDRESS_FAILURE) {
      props.showToast('Failed to update address info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoading(false);
      return false;
    } else {
      props.showToast('Address updated successfully.', { userMustDismiss: false, severity: 'success' });
      await props.getClientAddressesByClientId(clientId);
      return true;
    }
  };

  const createAddress = async values => {
    const newValues = {
      clientAddress: {
        clientAddressId: 0,
        clientRecordId: clientId,
        isPrimary: true
      },
      address: {
        addressId: 0,
        addressLine1: values.addressLine1 ? values.addressLine1 : '',
        addressLine2: values.addressLine2 ? values.addressLine2 : '',
        addressLine3: values.addressLine3 ? values.addressLine3 : '',
        city: values.city ? values.city : '',
        county: values.county ? values.county : '',
        zipcode: values.zipcode ? values.zipcode : '',
        countryId: values.countryId ? values.countryId : '',
        provinceId: values.provinceId ? values.provinceId : '',
        addressTypeId: "67a86dee-2aa4-4975-9df0-95368dfec694",
        addedBy: 1,
        addedOn: new Date(),
        modifiedBy: 1,
        modifiedOn: new Date()
      }
    };

    const resp = await props.postClientAddress(newValues);

    if (resp.type === POST_CLIENT_ADDRESS_FAILURE) {
      props.showToast('Failed to create new address, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    } else {
      props.showToast('Address created successfully.', { userMustDismiss: false, severity: 'success' });
      await props.getClientAddressesByClientId(clientId);
      return true;
    }
  };

  const deleteAddress = async address => {
    const deleteAddressResp = await props.deleteClientAddress(address.original.clientAddressId);
    if (deleteAddressResp.type === DELETE_CLIENT_ADDRESS_FAILURE) {
      props.showToast('Failed to delete the address. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    } else {
      props.showToast('Address deleted successfully.', { userMustDismiss: false, severity: 'success' });
      await props.getClientAddressesByClientId(clientId);
    }
  };

  return (
    <>
      {clientAddresses && (
        <>
          <Card>
            <Box>
              <CardContent className={props.classes.cardContainer}>
                <div className="row justify-space-between align-center">
                  <div className={props.classes.iconRow}>
                    <Tooltip title={'Addresses'} key={'Addresses'} placement="right">
                      <FontAwesomeIcon icon={['fas', 'house']} size="2x" />
                    </Tooltip>
                    <Typography variant="h5" sx={{ paddingLeft: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
                      Client Addresses
                    </Typography>
                  </div>
                </div>
              </CardContent>
              <Box sx={{ padding: '10px', marginLeft: '30px' }}>
                {!isLoading && (
                  <ClientAddressManagementTable
                    classes={props.classes}
                    columns={addressColumns}
                    rows={clientAddresses || []}
                    createRow={createAddress}
                    updateRow={updateAddress}
                    deleteRow={deleteAddress}
                    customAddText={'Add Address'}
                    hideEdit={hideEdit}
                    countriesEnum={countries}
                    provincesEnum={provinces}
                  />
                )}
                {isLoading && <Spinner />}
              </Box>
            </Box>
          </Card>
        </>
      )}
    </>
  );
};

AddressesCard.propTypes = {
  classes: PropTypes.object.isRequired,
  clientId: PropTypes.string.isRequired,
  clientAddresses: PropTypes.array,
  enumerations: PropTypes.object
};

const mapStateToProps = state => {
  const { countries, provinces } = state.enumerations.allEnums;
  
  return {
    countriesEnum: countries,
    provincesEnum: provinces
  };
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, {
    postClientAddress,
    updateClientAddress,
    deleteClientAddress,
    getClientAddressesByClientId,
    showToast
  })
)(AddressesCard);
